import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (event) => {
      const { lat, lng } = event.latlng;
      onMapClick({ lat, lng });
    }
  });
  return null;
};

const TravelSafetyMap = ({ 
  center = [20, 0], 
  zoom = 2, 
  onMapClick, 
  safetyZones = [],
  className = ''
}) => {
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [selectedZone, setSelectedZone] = useState(null);
  
  const [zones, setZones] = useState([
    {
      id: 'nyc-manhattan',
      name: 'Manhattan, NYC',
      positions: [
        [40.7589, -74.0278],
        [40.7828, -74.0278],
        [40.7828, -73.9442],
        [40.7589, -73.9442]
      ],
      color: '#4CAF50',
      fillColor: '#4CAF50',
      fillOpacity: 0.3,
      level: 'safe',
      safetyInfo: {
        crimeRate: 'Low',
        policePresence: 'High',
        touristSafety: 'Very Safe',
        description: 'Manhattan is generally very safe for tourists with excellent police presence and low crime rates in tourist areas.',
        recommendations: [
          'Stay in well-lit areas at night',
          'Keep valuables secure',
          'Use official transportation'
        ],
        lastUpdated: '2024-01-15'
      }
    },
    {
      id: 'london-central',
      name: 'Central London',
      positions: [
        [51.4994, -0.1746],
        [51.5194, -0.1746],
        [51.5194, -0.0746],
        [51.4994, -0.0746]
      ],
      color: '#FFC107',
      fillColor: '#FFC107',
      fillOpacity: 0.3,
      level: 'caution',
      safetyInfo: {
        crimeRate: 'Moderate',
        policePresence: 'Moderate',
        touristSafety: 'Generally Safe',
        description: 'Central London requires moderate caution due to pickpocketing and occasional street crime in crowded areas.',
        recommendations: [
          'Watch for pickpockets in tourist areas',
          'Avoid displaying expensive items',
          'Stay aware of surroundings on public transport'
        ],
        lastUpdated: '2024-01-10'
      }
    },
    {
      id: 'tokyo-shibuya',
      name: 'Shibuya, Tokyo',
      positions: [
        [35.6580, 139.6900],
        [35.6680, 139.6900],
        [35.6680, 139.7100],
        [35.6580, 139.7100]
      ],
      color: '#4CAF50',
      fillColor: '#4CAF50',
      fillOpacity: 0.3,
      level: 'safe',
      safetyInfo: {
        crimeRate: 'Very Low',
        policePresence: 'High',
        touristSafety: 'Very Safe',
        description: 'Shibuya is extremely safe with very low crime rates and excellent emergency services.',
        recommendations: [
          'Follow local customs and etiquette',
          'Keep emergency contact numbers handy',
          'Learn basic Japanese phrases'
        ],
        lastUpdated: '2024-01-12'
      }
    },
    {
      id: 'sample-danger-zone',
      name: 'High Risk Area',
      positions: [
        [33.3152, 44.3661],
        [33.4152, 44.3661],
        [33.4152, 44.4661],
        [33.3152, 44.4661]
      ],
      color: '#F44336',
      fillColor: '#F44336',
      fillOpacity: 0.3,
      level: 'unsafe',
      safetyInfo: {
        crimeRate: 'High',
        policePresence: 'Low',
        touristSafety: 'Not Recommended',
        description: 'This area has elevated security risks and is not recommended for tourists.',
        recommendations: [
          'Avoid travel to this area',
          'Contact local authorities if travel is necessary',
          'Register with embassy/consulate'
        ],
        lastUpdated: '2024-01-08'
      }
    }
  ]);

  const activeZones = safetyZones.length > 0 ? safetyZones : zones;

  const handleMapClick = (clickData) => {
    if (onMapClick) {
      onMapClick(clickData);
    }
  };

  const handleZoneClick = (zone) => {
    setSelectedZone(zone);
    if (onMapClick) {
      onMapClick({
        type: 'zone',
        zone: zone,
        lat: zone.positions[0][0],
        lng: zone.positions[0][1]
      });
    }
  };

  const getSafetyLevelColor = (level) => {
    switch (level) {
      case 'safe': return 'var(--color-success)';
      case 'caution': return 'var(--color-warning)';
      case 'unsafe': return 'var(--color-error)';
      default: return 'var(--color-text-muted)';
    }
  };

  const getSafetyLevelText = (level) => {
    switch (level) {
      case 'safe': return 'Safe';
      case 'caution': return 'Caution';
      case 'unsafe': return 'Unsafe';
      default: return 'Unknown';
    }
  };

  useEffect(() => {
    const handleResize = () => {
      window.dispatchEvent(new Event('resize'));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`travel-safety-map ${className}`} style={{ 
      width: '100%', 
      height: '400px',
      minHeight: '300px',
      position: 'relative'
    }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ 
          width: '100%', 
          height: '100%',
          minHeight: '300px'
        }}
        zoomControl={true}
        scrollWheelZoom={true}
        doubleClickZoom={true}
        dragging={true}
        touchZoom={true}
        bounceAtZoomLimits={true}
        maxBounds={[[-90, -180], [90, 180]]}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={18}
          minZoom={1}
        />
        
        <MapClickHandler onMapClick={handleMapClick} />
        
        {activeZones.map((zone) => (
          <Polygon
            key={zone.id}
            positions={zone.positions}
            pathOptions={{
              color: zone.color,
              fillColor: zone.fillColor,
              fillOpacity: zone.fillOpacity,
              weight: 2,
              opacity: 0.8
            }}
            eventHandlers={{
              click: () => handleZoneClick(zone)
            }}
          />
        ))}
      </MapContainer>
      
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '13px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
        minWidth: '120px'
      }}>
        <div style={{ 
          fontWeight: 'bold', 
          marginBottom: '8px',
          color: '#333',
          borderBottom: '1px solid #eee',
          paddingBottom: '4px'
        }}>
          Safety Levels
        </div>
        <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            backgroundColor: '#4CAF50', 
            width: '14px', 
            height: '14px', 
            display: 'inline-block', 
            marginRight: '8px',
            borderRadius: '2px'
          }}></span>
          <span style={{ color: '#4CAF50', fontWeight: '500' }}>Safe</span>
        </div>
        <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            backgroundColor: '#FFC107', 
            width: '14px', 
            height: '14px', 
            display: 'inline-block', 
            marginRight: '8px',
            borderRadius: '2px'
          }}></span>
          <span style={{ color: '#F57C00', fontWeight: '500' }}>Caution</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ 
            backgroundColor: '#F44336', 
            width: '14px', 
            height: '14px', 
            display: 'inline-block', 
            marginRight: '8px',
            borderRadius: '2px'
          }}></span>
          <span style={{ color: '#F44336', fontWeight: '500' }}>Unsafe</span>
        </div>
        <div style={{ 
          fontSize: '11px', 
          color: '#666', 
          marginTop: '8px',
          fontStyle: 'italic'
        }}>
          Click zones for details
        </div>
      </div>

      {selectedZone && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          maxWidth: '300px',
          minWidth: '250px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 'var(--spacing-3)'
          }}>
            <h3 style={{ 
              margin: 0, 
              color: '#333',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              {selectedZone.name}
            </h3>
            <button 
              onClick={() => setSelectedZone(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#666',
                padding: '0',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            marginBottom: 'var(--spacing-3)'
          }}>
            <span style={{ 
              backgroundColor: getSafetyLevelColor(selectedZone.level), 
              color: 'white',
              padding: 'var(--spacing-1) var(--spacing-2)',
              borderRadius: 'var(--radius-base)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-bold)'
            }}>
              {getSafetyLevelText(selectedZone.level).toUpperCase()}
            </span>
          </div>

          <div style={{ marginBottom: 'var(--spacing-3)' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--spacing-2)',
              fontSize: 'var(--font-size-xs)'
            }}>
              <div>
                <strong>Crime Rate:</strong><br />
                <span className="text-muted">{selectedZone.safetyInfo.crimeRate}</span>
              </div>
              <div>
                <strong>Police Presence:</strong><br />
                <span className="text-muted">{selectedZone.safetyInfo.policePresence}</span>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Tourist Safety:</strong><br />
                <span className="text-muted">{selectedZone.safetyInfo.touristSafety}</span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 'var(--spacing-3)' }}>
            <p className="text-secondary" style={{ 
              margin: 0, 
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-relaxed)'
            }}>
              {selectedZone.safetyInfo.description}
            </p>
          </div>

          <div style={{ marginBottom: 'var(--spacing-3)' }}>
            <strong className="text-primary" style={{ fontSize: 'var(--font-size-sm)' }}>Recommendations:</strong>
            <ul className="text-muted" style={{ 
              margin: 'var(--spacing-1) 0 0 0', 
              paddingLeft: 'var(--spacing-4)',
              fontSize: 'var(--font-size-xs)'
            }}>
              {selectedZone.safetyInfo.recommendations.map((rec, index) => (
                <li key={index} style={{ marginBottom: 'var(--spacing-1)' }}>{rec}</li>
              ))}
            </ul>
          </div>

          <div className="text-xs text-light" style={{ 
            borderTop: `1px solid var(--color-border-light)`,
            paddingTop: 'var(--spacing-2)'
          }}>
            Last updated: {selectedZone.safetyInfo.lastUpdated}
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelSafetyMap;